import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { loginUser, getCurrentUser } from '../services/apiService';

export interface User {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
    role: string;
    companyId: number;
    status?: string;
    createdAt?: string;
    updatedAt?: string;
    company?: {
        name: string;
        logo?: string;
    };
}

interface AuthContextType {
    user: User | null;
    token: string | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    login: (email: string, password: string) => Promise<void>;
    logout: () => void;
    error: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
    children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(!!token);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const navigate = useNavigate();

    useEffect(() => {
        const verifyToken = async () => {
            setIsLoading(true);
            try {
                if (token) {
                    const userData = await getCurrentUser();
                    setUser(userData);
                    setIsAuthenticated(true);
                }
            } catch (err) {
                console.error('Authentication error:', err);
                setToken(null);
                setUser(null);
                setIsAuthenticated(false);
                localStorage.removeItem('token');
            } finally {
                setIsLoading(false);
            }
        };

        verifyToken();
    }, [token]);

    const login = async (email: string, password: string) => {
        setError(null);
        setIsLoading(true);
        try {
            const { user, token } = await loginUser(email, password);
            setToken(token);
            setUser(user);
            setIsAuthenticated(true);
            localStorage.setItem('token', token);
            navigate('/rooms'); // Changed from /my-room-screen
        } catch (err: any) {
            console.error('Login failed:', err);
            setError(err.message || 'Invalid email or password.');
            setToken(null);
            setUser(null);
            setIsAuthenticated(false);
        } finally {
            setIsLoading(false);
        }
    };

    const logout = () => {
        localStorage.removeItem('token');
        setToken(null);
        setUser(null);
        setIsAuthenticated(false);
        navigate('/login');
    };

    const value = {
        user,
        token,
        isAuthenticated,
        isLoading,
        login,
        logout,
        error,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
